import { useRef, useState } from "react";
import { Link } from "wouter";
import { useTranslation } from "react-i18next";
import { Check, Minus, Package, Plus, ShoppingCart } from "lucide-react";
import { cn, formatKD } from "@/lib/utils";
import { type Product } from "@/lib/types";
import { useShopCart } from "@/lib/cart";
import { useShopMarket } from "@/lib/shop-market";
import { flyToCart } from "@/lib/fly-to-cart";

/**
 * Shop product card — visual twin of the home page's ServicePhotoCard
 * (same shell, floating price badge, category chip). Real product photos are
 * pending, so the no-image fallback is a first-class state.
 */
export function ProductCard({ product }: { product: Product }) {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === "ar";
  const { addItem, items, decrementItem } = useShopCart();
  const { b2bKey, basePath } = useShopMarket();
  const inCartQty = items.find((i) => i.productId === product.id)?.quantity ?? 0;
  const [justAdded, setJustAdded] = useState(false);
  const addedTimer = useRef<ReturnType<typeof setTimeout>>();
  const imageRef = useRef<HTMLDivElement>(null);

  const name = isRtl ? product.nameAr : product.nameEn;
  const category = isRtl ? product.categoryNameAr : product.categoryNameEn;
  const description = isRtl ? product.descriptionAr : product.descriptionEn;

  return (
    // isolate + rounded image wrapper: WebKit doesn't clip transformed
    // children (the hover scale) to an ancestor's border-radius.
    <li className="group relative isolate rounded-3xl border border-border bg-card overflow-hidden hover:border-primary/60 hover:shadow-xl hover:shadow-primary/5 hover:-translate-y-1 transition-all duration-300">
      <Link
        href={`${basePath}/${product.slug}`}
        className="flex h-full flex-col"
        data-testid={`shop-product-${product.slug}`}
      >
        <div ref={imageRef} className="relative h-44 overflow-hidden rounded-t-3xl">
          {product.imageUrl ? (
            <img
              src={product.imageUrl}
              alt={name}
              loading="lazy"
              className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center bg-muted">
              <Package className="h-10 w-10 text-muted-foreground/40" />
            </div>
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-black/5 to-transparent" aria-hidden="true" />
          <span className="absolute bottom-3 start-3 z-10 u-eyebrow rounded-full bg-black/45 text-white backdrop-blur-sm px-3 py-1.5">
            {category}
          </span>
          <span className="absolute bottom-3 end-3 z-10 flex items-baseline gap-1 rounded-full bg-card/95 backdrop-blur-sm px-3.5 py-1.5 shadow-md">
            <span className="font-display font-bold text-primary">{formatKD(product.price)}</span>
            <span className="text-[10px] text-muted-foreground">{t("services.currency")}</span>
          </span>
          {!product.inStock && (
            <span className="absolute top-3 start-3 z-10 rounded-full bg-destructive/90 text-destructive-foreground text-[10px] font-semibold px-2.5 py-1">
              {t("shop.outOfStock")}
            </span>
          )}
        </div>

        <div className="flex flex-col flex-1 p-5">
          <div className="flex items-baseline justify-between gap-2 mb-1.5">
            <h3 className="font-display font-bold text-lg group-hover:text-primary transition-colors">
              {name}
            </h3>
            {product.size && (
              <span className="text-xs text-muted-foreground whitespace-nowrap">{product.size}</span>
            )}
          </div>
          <p className="text-sm text-muted-foreground leading-relaxed line-clamp-2 mb-5">
            {description}
          </p>
          <div className="mt-auto flex items-center justify-between gap-3">
            {product.brand ? (
              <span className="inline-flex items-center rounded-full border border-border px-2.5 py-1 text-xs text-muted-foreground whitespace-nowrap">
                {product.brand}
              </span>
            ) : (
              <span />
            )}
            <span className="flex items-center gap-2">
              {product.inStock && inCartQty === 0 && (
                <button
                  type="button"
                  aria-label={t("shop.addToCart")}
                  onClick={(e) => {
                    // The whole card is a Link — keep the tap on the button.
                    e.preventDefault();
                    e.stopPropagation();
                    addItem(product, 1, b2bKey);
                    flyToCart(imageRef.current, product.imageUrl);
                    setJustAdded(true);
                    clearTimeout(addedTimer.current);
                    addedTimer.current = setTimeout(() => setJustAdded(false), 1200);
                  }}
                  className={cn(
                    "inline-flex h-8 w-8 items-center justify-center rounded-full border transition-all duration-300",
                    justAdded
                      ? "border-green-500 bg-green-500 text-white scale-110"
                      : "border-primary/40 text-primary hover:bg-primary hover:text-primary-foreground",
                  )}
                  data-testid={`quick-add-${product.slug}`}
                >
                  {justAdded ? <Check className="h-4 w-4" /> : <ShoppingCart className="h-4 w-4" />}
                </button>
              )}
              {product.inStock && inCartQty > 0 && (
                // In-cart: the button becomes a full stepper right on the card.
                <span
                  className="inline-flex items-center rounded-full bg-primary text-primary-foreground"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                  data-testid={`quick-stepper-${product.slug}`}
                >
                  <button
                    type="button"
                    aria-label="-"
                    className="flex h-8 w-8 items-center justify-center rounded-full hover:bg-primary-foreground/15 transition-colors"
                    onClick={() => decrementItem(product.id)}
                    data-testid={`quick-minus-${product.slug}`}
                  >
                    <Minus className="h-3.5 w-3.5" />
                  </button>
                  <span className="w-5 text-center text-xs font-bold tabular-nums">{inCartQty}</span>
                  <button
                    type="button"
                    aria-label="+"
                    className="flex h-8 w-8 items-center justify-center rounded-full hover:bg-primary-foreground/15 transition-colors disabled:opacity-40"
                    disabled={inCartQty >= product.stockQuantity}
                    onClick={() => addItem(product, 1, b2bKey)}
                    data-testid={`quick-plus-${product.slug}`}
                  >
                    <Plus className="h-3.5 w-3.5" />
                  </button>
                </span>
              )}
              <span className="inline-flex items-center rounded-full bg-primary text-primary-foreground text-xs font-semibold px-4 py-2 group-hover:bg-secondary transition-colors">
                {t("shop.viewProduct")}
              </span>
            </span>
          </div>
        </div>
      </Link>
    </li>
  );
}
